import useMediaQueries from 'app/hooks/useMediaQueries';
import { NoResultsFound } from 'Components/NoResultsFound';
import Spinner from 'Components/Spinner';
import { createContext, MouseEventHandler, ReactNode } from 'react';
import { GridCell } from './GridCell';
import { GridRow, RowRender } from './GridRow';
import Header from './Header';

import './style.css';

export type TGridCell = {
  id: string;
  columnId: string;
  rowId: number;
  width: string;
  wrapCell: boolean;
  render: ReactNode;
};

export type TGridHeader = {
  id?: string;
  render: ReactNode;
  width?: string;
  className?: string;
  justifyContent?: 'center' | 'start' | 'end';
  mobile?: boolean;
};

export const GridContext = createContext<{
  data?: { id: number }[];

  onRowClick?: (p: {
    data: any;
    target: HTMLElement;
    row: HTMLElement;
  }) => void;
}>({} as any);
function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear

  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Removing temporary elements from the DOM
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
}
export function Grid<Data extends { id: any }>({
  onRowClick,
  data,
  isLoading,
  lastRow,
  children,
  columns,
  footer,

  firstRow,
  headerClassName = 'bg-gray-700  font-semibold text-white',
  renderCells,
  gridRowWrap = true,
}: {
  onSortChange?: () => void;
  data?: Data[];
  isLoading?: boolean;
  isFetching?: boolean;
  headerClassName?: string;
  onRowClick?: (p: {
    data: Data;
    target: HTMLElement;
    row: HTMLElement;
  }) => void;
  lastRow?: ReactNode;
  firstRow?: ReactNode;
  gridRowWrap?: boolean;
  footer?: ReactNode;
  columns?: (string | TGridHeader)[];
  children?: (d: Data, rowIndex: number) => ReactNode;
  renderCells?: (d: Data) => ReactNode[];
}) {
  const dataAvailable = data;

  const onGridClick: MouseEventHandler<HTMLDivElement> = (e) => {
    if (onRowClick && data) {
      const row = (e.target as HTMLElement).closest(
        '.grid-row',
      ) as HTMLElement | null;

      const index = row?.getAttribute('data-index');
      let item = index && (data[+index] as Data);

      if (index && item && row) {
        onRowClick({ data: item, target: e.target as HTMLElement, row });
      }
    }
  };
  const { lg_up: isDesktop } = useMediaQueries();

  const gridHeaders: TGridHeader[] = (columns || [])
    .map((_) =>
      typeof _ === 'string'
        ? { render: _, width: 'minmax(0, 1fr)' }
        : { ..._, width: _.width ? _.width : 'minmax(0, 1fr)' },
    )
    .filter((col) => (isDesktop ? true : !!col.mobile));

  const gridTemplateColumns = gridHeaders.map((_) => _.width).join(' ');
  const scrollbarWidth = getScrollbarWidth();

  return (
    <GridContext.Provider
      value={{
        data: dataAvailable,
        onRowClick,
      }}
    >
      <div className="flex-1 flex flex-col h-full overflow-hidden w-full text-sm">
        {isDesktop && (
          <Header
            scrollbarWidth={scrollbarWidth}
            gridTemplateColumns={gridTemplateColumns}
            columns={columns}
            className={headerClassName}
          />
        )}

        <div
          className={`overflow-y-auto grid w-full place-content-start flex-1 `}
          style={{
            marginRight: `-${scrollbarWidth}px`,
            gridTemplateColumns,
            opacity: isLoading ? '0.5' : '1',
          }}
          onClick={onGridClick}
        >
          {!dataAvailable && (
            <GridRow hover={false}>
              <GridCell border="" fillRow={true}>
                <Spinner />
              </GridCell>
            </GridRow>
          )}

          {dataAvailable && (
            <>
              {firstRow}

              {dataAvailable.length === 0 && (
                <GridCell fillRow={true} border="">
                  <NoResultsFound subTitle="No items found" />
                </GridCell>
              )}

              {(children || renderCells) &&
                dataAvailable.map((_, rowIndex) =>
                  gridRowWrap ? (
                    <GridRow
                      key={`${_.id}-${rowIndex}`}
                      pointer={!!onRowClick}
                      hover={true}
                      index={rowIndex}
                      id={_.id}
                    >
                      <RowRender
                        children={children}
                        data={_}
                        columns={gridHeaders}
                        rowIndex={rowIndex}
                        renderCells={renderCells}
                      />
                    </GridRow>
                  ) : (
                    <RowRender
                      key={`${_.id}-${rowIndex}`}
                      children={children}
                      data={_}
                      columns={gridHeaders}
                      rowIndex={rowIndex}
                      renderCells={renderCells}
                    />
                  ),
                )}

              {lastRow}
            </>
          )}
        </div>
        {footer}
      </div>
    </GridContext.Provider>
  );
}

export { GridCell } from './GridCell';
export { GridRow } from './GridRow';
